import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/lib/constants";

const ease = [0.16, 1, 0.3, 1];

export const Services = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-cream py-24 sm:py-32 lg:py-40 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 lg:mb-20"
        >
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-bronze font-serif text-2xl italic">§</span>
              <span className="eyebrow">Nos expertises</span>
            </div>
            <h2 className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-normal">
              <span className="sr-only">Nos Services de Rénovation à Genève : Plâtrerie, Peinture, Isolation, Stucs, Moulures, Façade. </span>
              Six métiers,
              <br />
              <span className="italic text-bronze">une seule</span> exigence.
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="text-charcoal-muted text-base lg:text-lg leading-relaxed font-light">
              Appartements, maisons, espaces professionnels.
              Tous vos projets à Genève, avec la même rigueur artisanale.
            </p>
          </div>
        </motion.div>

        {/* Editorial horizontal numbered list */}
        <div
          className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16"
          onMouseLeave={() => setHovered(null)}
        >
          {/* Left — service rows */}
          <ul className="lg:col-span-7 border-t border-border">
            {SERVICES.map((s, i) => (
              <motion.li
                key={s.id}
                data-testid={`service-row-${s.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease, delay: i * 0.05 }}
                onMouseEnter={() => setHovered(i)}
                className="group border-b border-border"
              >
                <a
                  href="#devis"
                  className="grid grid-cols-12 items-baseline gap-6 py-7 lg:py-9 transition-all duration-500 hover:pl-4"
                >
                  <span className="col-span-2 font-serif text-bronze text-base lg:text-lg tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="col-span-8 lg:col-span-7">
                    <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-charcoal group-hover:text-bronze font-normal leading-tight transition-colors duration-500">
                      <span className="sr-only">{s.h3 || s.title}. </span>
                      <span aria-hidden="true">{s.title}</span>
                    </h3>
                    <p className="mt-2 text-charcoal-muted text-sm lg:text-base font-light leading-relaxed max-w-md overflow-hidden max-h-0 group-hover:max-h-32 group-hover:mt-3 transition-all duration-700">
                      {s.description}
                    </p>
                  </div>
                  <span className="col-span-2 lg:col-span-3 flex items-center justify-end text-charcoal-muted group-hover:text-bronze transition-colors duration-500">
                    <ArrowUpRight
                      className="w-5 h-5 lg:w-6 lg:h-6 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
                      strokeWidth={1}
                    />
                  </span>
                </a>
              </motion.li>
            ))}
          </ul>

          {/* Right — image reveal panel (desktop only) */}
          <div className="hidden lg:block lg:col-span-5 relative">
            <div className="sticky top-32 aspect-[3/4] bg-cream-alt overflow-hidden">
              <AnimatePresence mode="wait">
                {hovered !== null ? (
                  <motion.img
                    key={SERVICES[hovered].id}
                    src={SERVICES[hovered].image}
                    alt={`${SERVICES[hovered].h3 || SERVICES[hovered].title} par DEM BAT à Grand-Lancy Genève`}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="text-center px-8">
                      <div className="font-serif text-charcoal text-7xl italic">DEM</div>
                      <div className="mt-4 text-charcoal-muted text-xs uppercase tracking-[0.3em]">
                        Survolez un service
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

              {hovered !== null && (
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-bronze-light text-[10px] uppercase tracking-widest mb-2">
                    {String(hovered + 1).padStart(2, "0")}
                  </div>
                  <div className="font-serif text-white text-2xl lg:text-3xl">
                    {SERVICES[hovered].title}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile descriptions are always visible — handled inline above with max-h trick.
            On smaller screens we always show the description: */}
        <style>{`
          @media (max-width: 1023px) {
            [data-testid^="service-row-"] p {
              max-height: 200px !important;
              margin-top: 0.5rem !important;
            }
          }
        `}</style>
      </div>
    </section>
  );
};
